import { Component, ChangeDetectionStrategy, ChangeDetectorRef, Input, OnDestroy } from '@angular/core';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import { formatWeekdayLong } from '@cf-shared/date-format';
import { CfPopoverBase } from 'components/popover-host/popover-interface';
import { PopoverController } from 'providers/popover-controller';
import { Schedule_ItemType, ScheduleRule, getNextWorkoutDateISO, getEffectiveRuleTime, countMealPerDayFromScheduleRules } from '@cf-shared/data-model/schedule';
import { Log_Entry, LogRecordType, LogRecordState, getEntryLocalTimeInDesiredTimeZone, setEntryLocalTime } from '@cf-shared/data-model/user-log';
import { isoTimeToHours, setDecimalHour } from '@cf-shared/time';
import { UserDb } from 'services/user-db.service';
import { DatabaseWrapper, DocWatcher, TransactionId } from 'services/database-wrapper';
import { LoadingController } from 'providers/loading-controller';
import { DebugService } from 'services/debug.service';
import { ScheduleRuleService, ScheduleItemDisplayInfo, ScheduleEditScope, RescheduleMode, DayOption } from 'services/schedule-rule.service';
import { DestroyGuard } from 'services/browser-utils';
import { TelemetryService } from 'services/telemetry.service';
import { NotificationService } from 'services/notification.service';
import { SettingsService } from 'services/settings.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { LoggingService } from 'services/logging.service';
import { isCapacitorNative } from 'environments/environment-helpers';
import { formatRelativeDay, formatScheduledMealName, upperFirstTranslated, weekdayName } from 'util/formatting';

export type SchedulableLogRecordType =
  | LogRecordType.Workout
  | LogRecordType.Meal
  | LogRecordType.DayStart
  | LogRecordType.DayEnd
  | LogRecordType.WeighIn;

export enum RescheduleOption {
  ThisDayOnly = 'this-day-only',
  AllDays = 'all-days',
  ThisWeekday = 'this-weekday'
}

export interface RescheduleResult {
  success: boolean;
}

export type ReschedulePopoverParams =
  | {
      mode: RescheduleMode.LogEntry;
      entryType: SchedulableLogRecordType;
      entry: Log_Entry;
      mealIndex?: number; // only used when entryType === Meal
      allMealTimes_hr?: number[]; // sorted meal times in hours, used for redistribution when rescheduling past other meals
    }
  | {
      mode: RescheduleMode.ScheduleEditing;
      /** Display info from ScheduleRuleService - contains all data needed for editing */
      displayInfo: ScheduleItemDisplayInfo;
    };

@Component({
  selector: 'reschedule-popover',
  templateUrl: 'reschedule-popover.html',
  host: { 'class': 'reschedule-popover' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReschedulePopover extends CfPopoverBase implements OnDestroy {
  protected LL = LL;
  private docWatcher: DocWatcher;
  private destroyGuard = new DestroyGuard();
  @Input() params: ReschedulePopoverParams;

  protected selectedTime: string;
  protected selectedOption: RescheduleOption = RescheduleOption.ThisDayOnly;
  protected selectedWeekday: number | null = null; // 1-7 (Monday-Sunday)
  protected selectedDateISO: string | null = null; // actual date for ThisDayOnly mode
  protected selectedDayValue: string | number | null = null; // dropdown binding value
  protected availableWeekdays: DayOption[] = [];
  // other meals' effective times (excluding the one being rescheduled) — used to block exact-time collisions for meals
  protected otherMealsForConflict: { displayNumber: number; time_hr: number }[] = [];
  protected RescheduleOption = RescheduleOption;

  get isScheduleEditingMode(): boolean {
    return this.params?.mode === RescheduleMode.ScheduleEditing;
  }

  get isWorkout(): boolean {
    if (this.params?.mode === RescheduleMode.ScheduleEditing) {
      return this.params.displayInfo.itemType === Schedule_ItemType.Workout;
    }
    return this.params?.entryType === LogRecordType.Workout;
  }

  get isMeal(): boolean {
    if (this.params?.mode === RescheduleMode.ScheduleEditing) {
      return this.params.displayInfo.itemType === Schedule_ItemType.Meal;
    }
    return this.params?.entryType === LogRecordType.Meal;
  }

  /**
   * 1-based meal number of the other meal whose effective time exactly matches the
   * currently selected time, or null if no conflict. Two meals sharing a time cause
   * silent data loss downstream (see schedule.ts handleMeals, issue #3166), so the
   * popover blocks Save until the user picks a non-colliding time.
   */
  get conflictingMealNumber(): number | null {
    if (!this.isMeal || !this.selectedTime) return null;
    const target_hr = isoTimeToHours(this.selectedTime);
    const match = this.otherMealsForConflict.find(m => Math.abs(m.time_hr - target_hr) < 0.001);
    return match ? match.displayNumber : null;
  }

  get isWakeUpOrWeighIn(): boolean {
    if (this.params?.mode === RescheduleMode.ScheduleEditing) {
      return this.params.displayInfo.itemType === Schedule_ItemType.DayStart;
    }
    return this.params?.entryType === LogRecordType.DayStart ||
           this.params?.entryType === LogRecordType.WeighIn;
  }

  get title(): string {
    if (this.isWakeUpOrWeighIn) {
      return LL.reschedule.titleWakeUpWeighIn();
    }
    return LL.reschedule.title({ item: this.itemName });
  }

  get timeLabel(): TranslatedString {
    if (this.isWakeUpOrWeighIn) {
      return LL.reschedule.labels.wakeUp();
    }
    return LL.reschedule.labels.time();
  }

  get weighInTime(): string {
    if (!this.selectedTime) return '';
    const [hours, minutes] = this.selectedTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + 5;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;
    return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
  }

  get itemType(): Schedule_ItemType {
    if (!this.params) return Schedule_ItemType.Meal;
    if (this.params.mode === RescheduleMode.ScheduleEditing) {
      return this.params.displayInfo.itemType;
    }
    // convert LogRecordType to Schedule_ItemType
    switch (this.params.entryType) {
      case LogRecordType.Workout:
        return Schedule_ItemType.Workout;
      case LogRecordType.Meal:
        return Schedule_ItemType.Meal;
      case LogRecordType.DayStart:
      case LogRecordType.WeighIn:
        // WeighIn uses DayStart schedule rules
        return Schedule_ItemType.DayStart;
      case LogRecordType.DayEnd:
        return Schedule_ItemType.DayEnd;
    }
  }

  get itemName(): string {
    if (!this.params) return '';
    // in schedule editing mode, use the label from displayInfo
    if (this.params.mode === RescheduleMode.ScheduleEditing) {
      return this.params.displayInfo.label;
    }

    // in log-entry mode, derive from the log entry
    switch (this.params.entry.type) {
      case LogRecordType.Meal:
        // a generated name like "Meal 1" gets translated; a custom one is kept
        return formatScheduledMealName(this.params.entry.name) || LL.reschedule.labels.meal();
      case LogRecordType.Workout:
        return LL.dayItems.titles.workout();
      case LogRecordType.DayStart:
        return LL.dayItems.titles.wakeUp();
      case LogRecordType.DayEnd:
        return LL.dayItems.titles.sleep();
      case LogRecordType.WeighIn:
        return LL.dayItems.titles.weighIn();
      default:
        return LL.reschedule.labels.item();
    }
  }

  get itemTypeDisplay(): string {
    return this.itemName;
  }

  get currentTime(): string {
    if (!this.params) return '00:00';
    if (this.params.mode === RescheduleMode.ScheduleEditing) {
      // convert time_hr from displayInfo to HH:mm format
      const hours = Math.floor(this.params.displayInfo.time_hr);
      const minutes = Math.round((this.params.displayInfo.time_hr - hours) * 60);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } else {
      // extract time from entry
      let entryTime = getEntryLocalTimeInDesiredTimeZone(this.params.entry, this.debug.getNowLocal());
      // if WeighIn, show wake-up time (5 min earlier) since we're editing wake-up, not weigh-in
      if (this.params.entry.type === LogRecordType.WeighIn) {
        entryTime = entryTime.minus({ minutes: 5 });
      }
      return entryTime.toISOTime().substring(0, 5); // HH:mm format
    }
  }

  get currentDay(): string {
    if (!this.params) return '';
    if (this.params.mode === RescheduleMode.ScheduleEditing) {
      // get weekday from displayInfo
      if (this.params.displayInfo.weekday) {
        return weekdayName(this.params.displayInfo.weekday);
      }
      // for daily rules, use today
      return formatWeekdayLong(this.debug.getNowLocal(), BOOT_LOCALE);
    } else {
      // extract day name from entry
      const entryTime = getEntryLocalTimeInDesiredTimeZone(this.params.entry, this.debug.getNowLocal());
      return formatWeekdayLong(entryTime, BOOT_LOCALE); // full day name like "Friday"
    }
  }

  // selected weekday name, falling back to original day when no selection made
  private get selectedDayName(): string {
    if (this.selectedWeekday != null) {
      return weekdayName(this.selectedWeekday);
    }
    return this.currentDay;
  }

  get optionThisDayExplanation(): string {
    return LL.reschedule.options.thisDayOnly.description({ weekday: this.selectedDayName });
  }

  get optionAllDaysExplanation(): string {
    if (this.isWakeUpOrWeighIn) {
      return LL.reschedule.options.allDays.descriptionWakeUp();
    }
    if (this.isWorkout) {
      return LL.reschedule.options.allDays.descriptionWorkout();
    }
    return LL.reschedule.options.allDays.description({ itemName: this.itemName.toLowerCase() });
  }

  get optionThisWeekdayExplanation(): string {
    if (this.isWorkout) {
      return LL.reschedule.options.thisWeekday.descriptionWorkout({ weekday: this.selectedDayName });
    }
    if (this.isWakeUpOrWeighIn) {
      return LL.reschedule.options.thisWeekday.descriptionWakeUp({ weekday: this.selectedDayName });
    }
    return LL.reschedule.options.thisWeekday.description({ itemName: this.itemName.toLowerCase(), weekday: this.selectedDayName });
  }

  static async present(
    popoverCtrl: PopoverController,
    targetElement: HTMLElement,
    params: ReschedulePopoverParams
  ): Promise<RescheduleResult | null> {
    const result = await popoverCtrl.present(
      ReschedulePopover,
      { params }, // Wrap in object so it's set as single @Input() params property
      {
        animate: true,
        targetFromElement: targetElement,
        showBackdrop: true,
      }
    );

    return result.data;
  }

  constructor(
    private popoverCtrl: PopoverController,
    private userDb: UserDb,
    private loadingCtrl: LoadingController,
    private debug: DebugService,
    private scheduleRuleService: ScheduleRuleService,
    private telemetry: TelemetryService,
    private notificationService: NotificationService,
    private settings: SettingsService,
    private cloudFunctions: CloudFunctionsService,
    private cdr: ChangeDetectorRef,
    private logging: LoggingService,
  ) {
    super();
  }

  ngOnDestroy() {
    this.destroyGuard.destroy();
    this.docWatcher?.destroy();
  }

  async ngOnInit() {
    // ensure params is set before proceeding
    if (!this.params) return;

    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.destroyGuard.isDestroyed) return;
        this.cdr.markForCheck();
      });
    }

    // initialize selected time with entry time
    this.selectedTime = this.currentTime;

    // set default option based on mode and itemType
    if (this.params.mode === RescheduleMode.ScheduleEditing) {
      // schedule editing mode - use itemType from displayInfo
      if (this.params.displayInfo.itemType === Schedule_ItemType.Workout) {
        this.selectedOption = RescheduleOption.ThisWeekday;
      } else {
        // Meal, DayStart, DayEnd
        this.selectedOption = RescheduleOption.AllDays;
      }
    } else {
      // log-entry mode - keep default ThisDayOnly
      this.selectedOption = RescheduleOption.ThisDayOnly;
    }

    // if this is a workout, populate available days
    if (this.isWorkout) {
      await this.loadAvailableDays();
    }

    // if this is a meal, populate other meals' times for collision detection
    if (this.isMeal) {
      if (this.params.mode === RescheduleMode.LogEntry
          && this.params.allMealTimes_hr
          && this.params.mealIndex != null
          && this.params.mealIndex >= 0
          && this.params.mealIndex < this.params.allMealTimes_hr.length) {
        // day view passes times sorted chronologically; displayed meal number = sorted index + 1
        const sourceIdx = this.params.mealIndex;
        this.otherMealsForConflict = this.params.allMealTimes_hr
          .map((t, i) => ({ displayNumber: i + 1, time_hr: t }))
          .filter((_, i) => i !== sourceIdx);
      } else if (this.params.mode === RescheduleMode.ScheduleEditing) {
        await this.loadOtherMealsForScheduleEditing(this.params);
      }
    }
  }

  // caller already gates on RescheduleMode.ScheduleEditing; params is passed through
  // so TypeScript narrows the discriminated union for displayInfo access.
  // NOTE: loop assumes meal itemReferences are contiguous 0..N-1; countMealPerDayFromScheduleRules
  // returns the distinct-reference count, so sparse refs (e.g. after a deleted meal) would miss
  // some meals. Current codebase paths (adjustScheduleToDeclaredState,
  // ScheduleRuleService.applyRecurringPatternReschedule) preserve contiguity; revisit if that
  // invariant changes.
  private async loadOtherMealsForScheduleEditing(
    params: Extract<ReschedulePopoverParams, { mode: RescheduleMode.ScheduleEditing }>
  ): Promise<void> {
    const schedule = await this.userDb.loadScheduleAsync();
    if (this.destroyGuard.isDestroyed) return;
    const numMeals = countMealPerDayFromScheduleRules(schedule.rules);
    const currentRef = params.displayInfo.itemReference;
    const todayISO = this.debug.getNowLocal().toISODate();
    const weekday = params.displayInfo.weekday ?? this.debug.getNowLocal().weekday;

    const others: { displayNumber: number; time_hr: number }[] = [];
    for (let ref = 0; ref < numMeals; ref++) {
      if (ref === currentRef) continue;
      const t = getEffectiveRuleTime(schedule.rules, Schedule_ItemType.Meal, todayISO, weekday, ref);
      // Program Settings labels meals by itemReference + 1
      if (t != null) others.push({ displayNumber: ref + 1, time_hr: t });
    }
    this.otherMealsForConflict = others;
    this.cdr.markForCheck();
  }

  protected onOptionChange(option: RescheduleOption) {
    this.selectedOption = option;
    if (this.isWorkout) {
      // reload available days since the options differ between ThisDayOnly and ThisWeekday
      void this.loadAvailableDays();
    }
  }

  protected onDaySelectionChange(value: string | number) {
    this.selectedDayValue = value;

    if (this.selectedOption === RescheduleOption.ThisDayOnly) {
      // in ThisDayOnly mode, value is dateISO
      const selectedDay = this.availableWeekdays.find(d => d.dateISO === value);
      if (selectedDay) {
        this.selectedWeekday = selectedDay.value;
        this.selectedDateISO = selectedDay.dateISO;
      }
    } else {
      // in ThisWeekday/AllDays mode, value is weekday number
      // must use Number() to convert since DOM may pass value as string
      this.selectedWeekday = Number(value);
      this.selectedDateISO = null;
    }
  }

  private async loadAvailableDays() {
    const today = this.debug.getNowLocal();
    const todayISO = today.toISODate();

    // determine current weekday and entry date based on mode
    let currentWeekday: number;
    let entryDateISO: string | undefined;
    if (this.params.mode === RescheduleMode.ScheduleEditing) {
      currentWeekday = this.params.displayInfo.weekday || today.weekday;
    } else {
      const entryDateTime = getEntryLocalTimeInDesiredTimeZone(this.params.entry, today);
      currentWeekday = entryDateTime.weekday;
      entryDateISO = entryDateTime.toISODate();
    }

    // determine which type of options to show:
    // - ScheduleEditing mode OR ThisWeekday option: abstract weekdays from weekly rules
    // - LogEntry mode + ThisDayOnly option: actual dates from log entries
    const useAbstractWeekdays = this.params.mode === RescheduleMode.ScheduleEditing ||
                                 this.selectedOption === RescheduleOption.ThisWeekday;

    if (useAbstractWeekdays) {
      // use abstract weekdays from weekly rules (same as Program Settings)
      const schedule = await this.userDb.loadScheduleAsync();
      this.availableWeekdays = this.scheduleRuleService.getWeekdayOptions({
        rules: schedule.rules,
        todayISO,
        currentWeekday,
      });

      // preserve user's selection when switching modes (e.g. ThisDayOnly → ThisWeekday)
      if (this.selectedWeekday != null) {
        const preserved = this.availableWeekdays.find(d => d.value === this.selectedWeekday && !d.disabled);
        if (!preserved) {
          this.selectedWeekday = currentWeekday;
        }
      } else {
        this.selectedWeekday = currentWeekday;
      }
      this.selectedDateISO = entryDateISO ?? null;
      this.selectedDayValue = this.selectedWeekday;
    } else {
      // LogEntry mode + ThisDayOnly: use actual dates for the next 7 days
      // load log entries to check for existing workouts
      const startDate = today.startOf('day');
      const endDate = today.plus({ days: 7 }).endOf('day');
      const logEntries = await this.userDb.getLogEntriesInRangeAsync(startDate, endDate, this.docWatcher, { inLogEntriesTimeZone: true });

      // build set of dates that have workouts (not skipped)
      const datesWithWorkouts = new Set<string>();
      for (const entry of logEntries) {
        if (entry.type === LogRecordType.Workout && entry.state !== LogRecordState.Skipped) {
          const entryDate = getEntryLocalTimeInDesiredTimeZone(entry, today).toISODate();
          datesWithWorkouts.add(entryDate);
        }
      }

      // generate options for the next 7 days
      this.availableWeekdays = [];
      for (let i = 0; i < 7; i++) {
        const date = today.plus({ days: i });
        const dateISO = date.toISODate();
        const hasWorkout = datesWithWorkouts.has(dateISO);
        const isCurrent = dateISO === entryDateISO;

        // format label: "Today", "Tomorrow", or "Mon, Jan 29", capitalized
        const label = upperFirstTranslated(formatRelativeDay(today, date));

        this.availableWeekdays.push({
          value: date.weekday,
          dateISO,
          label,
          hasWorkout,
          // disable if has workout, unless it's the current day being rescheduled
          disabled: hasWorkout && !isCurrent,
          isCurrent,
        });
      }

      // preserve user's selection when switching modes (e.g. ThisWeekday → ThisDayOnly)
      if (this.selectedWeekday != null) {
        const matchingDate = this.availableWeekdays.find(d => d.value === this.selectedWeekday && !d.disabled);
        if (matchingDate) {
          this.selectedWeekday = matchingDate.value;
          this.selectedDateISO = matchingDate.dateISO;
          this.selectedDayValue = matchingDate.dateISO;
        } else {
          this.selectedWeekday = currentWeekday;
          this.selectedDateISO = entryDateISO;
          this.selectedDayValue = this.selectedDateISO;
        }
      } else {
        this.selectedWeekday = currentWeekday;
        this.selectedDateISO = entryDateISO;
        this.selectedDayValue = this.selectedDateISO;
      }
    }

    this.cdr.markForCheck();
  }

  onWillDismiss(data: any) {
    // data will be set by onSaveClick after successful save
    if (data.reason !== 'save') {
      data.data = null;
    }
  }

  onTimeChange(time: string) {
    this.selectedTime = time;
  }

  // save the rescheduled time and (on native) refresh local + server notifications so the change applies before the next app open
  async onSaveClick() {
    // defensive: template already disables Save, but guard against programmatic calls
    if (this.conflictingMealNumber !== null) return;

    const loading = this.loadingCtrl.create({
      content: LL.common.loading.pleaseWait()
    });
    await loading.present();

    // kept for after the finally, so the error dialog is not shown under the loading spinner
    let saveFailed = false;
    let saveError: unknown;
    try {
      await this.applyReschedule();
      // if native, fire-and-forget reschedule so OS + server pick up the new time without waiting for app reopen
      if (isCapacitorNative()) {
        void this.notificationService.rescheduleAllAsync(
          this.userDb, this.settings, this.debug.getNowLocal(), this.cloudFunctions,
        );
      }
      // dismiss with success result
      void this.popoverCtrl.dismiss(this, {
        reason: 'save',
        data: { success: true }
      });
    } catch (error) {
      saveFailed = true;
      saveError = error;
      // dismiss with failure result
      void this.popoverCtrl.dismiss(this, {
        reason: 'save',
        data: { success: false }
      });
    } finally {
      await loading.dismiss();
    }
    // if the save failed, the person asked for this change and is waiting on it: without this
    // the popover just closed and the item stayed where it was, with no word as to why
    if (saveFailed) {
      this.logging.trackAbnormalException(saveError);
    }
  }

  onCancelClick() {
    void this.popoverCtrl.dismiss(this, { reason: 'cancel' });
  }

  onCloseClick() {
    void this.popoverCtrl.dismiss(this, { reason: 'cancel' });
  }

  private async applyReschedule() {
    const schedule = await this.userDb.loadScheduleAsync();
    const newTime_hr = isoTimeToHours(this.selectedTime);
    const todayISO = this.debug.getNowLocal().toISODate();
    let currentWeekday: number;
    let entryDateISO: string | undefined;
    let itemReference: number | undefined;

    if (this.params.mode === RescheduleMode.ScheduleEditing) {
      // for schedule editing mode, use displayInfo data
      currentWeekday = this.params.displayInfo.weekday || this.debug.getNowLocal().weekday;
      itemReference = this.params.displayInfo.itemReference;
    } else {
      // for log-entry mode, use entry data
      const entryDateTime = getEntryLocalTimeInDesiredTimeZone(this.params.entry, this.debug.getNowLocal());
      entryDateISO = entryDateTime.toISODate();
      currentWeekday = entryDateTime.weekday; // 1 = Monday, 7 = Sunday
      itemReference = this.params.mealIndex;
    }

    // map RescheduleOption to ScheduleEditScope
    const scopeMap: Record<RescheduleOption, ScheduleEditScope> = {
      [RescheduleOption.ThisDayOnly]: ScheduleEditScope.ThisDayOnly,
      [RescheduleOption.AllDays]: ScheduleEditScope.AllDays,
      [RescheduleOption.ThisWeekday]: ScheduleEditScope.ThisWeekday,
    };

    // if rescheduling a meal past other meals, redistribute times to maintain order
    const allMealTimes = this.params.mode === RescheduleMode.LogEntry ? this.params.allMealTimes_hr : undefined;
    const crossedOtherMeals = allMealTimes && itemReference != null && this.itemType === Schedule_ItemType.Meal &&
      this.scheduleRuleService.redistributeMealTimes({
        rules: schedule.rules,
        allMealTimes_hr: allMealTimes,
        sourceRef: itemReference,
        targetTime_hr: newTime_hr,
        scope: scopeMap[this.selectedOption],
        todayISO,
        entryDateISO,
        currentWeekday,
      });

    if (!crossedOtherMeals) {
      // single-item reschedule (no crossing, or non-meal items)
      this.scheduleRuleService.rescheduleItem({
        rules: schedule.rules,
        itemType: this.itemType,
        itemReference,
        scope: scopeMap[this.selectedOption],
        newTime_hr,
        mode: this.params.mode,
        todayISO,
        entryDateISO,
        selectedDateISO: this.selectedDateISO ?? undefined,
        currentWeekday,
        newWeekday: this.selectedWeekday ?? undefined,
      });
    }

    // if rescheduling DayStart/DayEnd, shift meals that would violate minimum gap constraints
    if (this.itemType === Schedule_ItemType.DayStart || this.itemType === Schedule_ItemType.DayEnd) {
      const oldTime_hr = isoTimeToHours(this.currentTime);
      this.scheduleRuleService.shiftMealsForDayBoundaryChange({
        rules: schedule.rules,
        changedBoundary: this.itemType,
        oldTime_hr,
        newTime_hr,
        scope: scopeMap[this.selectedOption],
        mode: this.params.mode,
        todayISO,
        entryDateISO,
        selectedDateISO: this.selectedDateISO ?? undefined,
        currentWeekday,
      });
    }

    // handle workout move exclusion for ThisDayOnly mode
    const isMovingToNewWeekday = this.isWorkout &&
      this.selectedWeekday !== null &&
      this.selectedWeekday !== currentWeekday;
    if (this.selectedOption === RescheduleOption.ThisDayOnly &&
        this.itemType === Schedule_ItemType.Workout &&
        isMovingToNewWeekday) {
      const originalDateISO = entryDateISO || todayISO;
      this.scheduleRuleService.addWorkoutMoveExclusion(schedule.rules, originalDateISO);
    }

    // Save the updated schedule
    const transaction = DatabaseWrapper.getTransactionId();
    await this.userDb.saveScheduleAsync(transaction);

    // update first scheduled workout date for email flow
    if (this.itemType === Schedule_ItemType.Workout) {
      const firstWorkoutDate = getNextWorkoutDateISO(schedule.rules, this.debug.getNowLocal());
      if (firstWorkoutDate) {
        this.telemetry.setUserProperties({ first_scheduled_workout_at: firstWorkoutDate });
      }
    }

    // if rescheduled a meal past other meals, move the food entry to the target time
    if (crossedOtherMeals && this.params.mode === RescheduleMode.LogEntry) {
      const entry = this.params.entry;
      const entryLocal = getEntryLocalTimeInDesiredTimeZone(entry, this.debug.getNowLocal());
      const targetTime_local = setDecimalHour(entryLocal.startOf('day'), newTime_hr);
      setEntryLocalTime(entry, targetTime_local);
      await this.userDb.saveEntryAsync(transaction, entry);
    }

    // if rescheduled a DayEnd/DayStart entry, update the log entry's time
    if (this.params.mode === RescheduleMode.LogEntry &&
        (this.itemType === Schedule_ItemType.DayEnd || this.itemType === Schedule_ItemType.DayStart)) {
      const entry = this.params.entry;
      const entryLocal = getEntryLocalTimeInDesiredTimeZone(entry, this.debug.getNowLocal());
      const targetTime_local = setDecimalHour(entryLocal.startOf('day'), newTime_hr);
      setEntryLocalTime(entry, targetTime_local);
      await this.userDb.saveEntryAsync(transaction, entry);
    }

    await this.userDb.clearBacklogCacheAsync();

    // sync declaredState.trainingDays with the updated schedule rules
    if (this.itemType === Schedule_ItemType.Workout) {
      await this.syncDeclaredStateTrainingDays(schedule.rules, todayISO, transaction);
    }
  }

  /**
   * Sync declaredState.trainingDays with schedule rules after workout reschedule.
   * This ensures the Program Settings page reflects the current schedule.
   */
  private async syncDeclaredStateTrainingDays(rules: ScheduleRule[], todayISO: string, transaction: TransactionId) {
    const declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
    const trainingDays = this.scheduleRuleService.getTrainingDaysFromRules(rules, todayISO);
    let modified = false;

    for (let i = 0; i < trainingDays.length; i++) {
      if (declaredState.trainingDays[i] !== trainingDays[i]) {
        declaredState.trainingDays[i] = trainingDays[i];
        modified = true;
      }
    }

    if (modified) {
      await this.userDb.saveDeclaredStateAsync(transaction);
    }
  }
}